import RichTextEditorFooter from "molecules/rich-text/rich-text-editor-footer";
import RichTextImageUploadModal from "molecules/rich-text/rich-text-image-upload-modal";
import React, { useEffect, useRef } from "react";
import ReactQuill from "react-quill";
import useQuill from "utilities/hooks/quill/use-quill";
import QuillConfig, {
    ChecklistItemNoteTextEditorQuillConfig,
} from "utilities/quill/quill-config";
import ChecklistItemNoteRichTextQuillToolbar from "organisms/checklist-item-notes/checklist-item-note-rich-text-quill-toolbar";
import FileUploadDestination from "utilities/enumerations/file-upload-destination";
import { RICH_TEXT_EDITOR_CSS_CLASS_NAME } from "molecules/rich-text/rich-text-editor";
import useUserUploadId from "utilities/hooks/domain/image-upload/use-user-upload-id";

export interface ChecklistItemNoteRichTextEditorProps {
    fileUploadDestination?: FileUploadDestination;
    disabled?: boolean;
    errors?: string[];
    value: string;
    label?: string | React.ReactNode;
    maxLength?: number;
    onBlur?: () => void;
    onFocus?: () => void;
    onChange: (newValue: string, rawValue: string) => void;
    placeholder?: string;
    preserveWhitespace?: boolean;
    setPlainTextLength?: React.Dispatch<React.SetStateAction<number>>;
    displayImageOption?: boolean;
}

ChecklistItemNoteTextEditorQuillConfig.registerCustomModules();

const CSS_CLASS_NAME = RICH_TEXT_EDITOR_CSS_CLASS_NAME;

const ChecklistItemNoteRichTextEditor: React.FC<
    ChecklistItemNoteRichTextEditorProps
> = (props: ChecklistItemNoteRichTextEditorProps) => {
    const {
        fileUploadDestination,
        disabled,
        errors,
        label,
        maxLength,
        onBlur,
        placeholder,
        preserveWhitespace,
        value,
        setPlainTextLength,
        displayImageOption,
    } = props;

    const reactQuillRef = useRef<ReactQuill>(null);
    const quillOptions = {
        ...props,
        allowImages: true,
    };

    const {
        className,
        closeImageModal,
        containerId,
        containerSelector,
        handleChange,
        handleFocus,
        handleImageConfirmed,
        handleSelectionChange,
        isImageModalOpen,
        plainTextValue,
        quillModules,
        toolbarId,
    } = useQuill(CSS_CLASS_NAME, quillOptions, reactQuillRef);

    useEffect(() => {
        setPlainTextLength && setPlainTextLength(plainTextValue.length);
    }, [plainTextValue.length, setPlainTextLength]);

    // ReactQuill adds a training \n, so subtract 1
    const plainTextLength =
        (reactQuillRef.current?.getEditor()?.getText().length ?? 1) - 1;

    const handleTextChange = (
        value: string,
        delta,
        source,
        editor: ReactQuill.UnprivilegedEditor
    ) => {
        const currentPlainTextLength =
            (reactQuillRef.current?.getEditor()?.getText().length ?? 1) - 1;

        const isPastMax = maxLength && currentPlainTextLength > maxLength;
        if (isPastMax) {
            const amountOverLimit = currentPlainTextLength - maxLength;

            if (amountOverLimit) {
                reactQuillRef.current
                    ?.getEditor()
                    ?.deleteText(maxLength, amountOverLimit);
                return;
            }
        }
        handleChange(value, delta, source, editor);
    };

    const { userUploadId } = useUserUploadId();
    return (
        <div className={className}>
            {label != null && (
                <label className={`${CSS_CLASS_NAME}__label`}>{label}</label>
            )}
            <ChecklistItemNoteRichTextQuillToolbar
                disabled={disabled}
                id={toolbarId}
                displayImageOption={displayImageOption}
            />
            <ReactQuill
                bounds={containerSelector}
                className={`${CSS_CLASS_NAME}__editor`}
                formats={QuillConfig.standardQuillFormats}
                id={containerId}
                modules={quillModules}
                onBlur={onBlur}
                onChange={handleTextChange}
                onChangeSelection={handleSelectionChange}
                onFocus={handleFocus}
                placeholder={placeholder}
                preserveWhitespace={preserveWhitespace}
                readOnly={disabled}
                ref={reactQuillRef}
                theme={QuillConfig.theme}
                value={value}
            />
            <RichTextEditorFooter
                baseClassName={CSS_CLASS_NAME}
                currentLength={plainTextLength}
                errors={errors}
                maxLength={maxLength}
            />
            <RichTextImageUploadModal
                fileUploadDestination={fileUploadDestination}
                baseClassName={`${CSS_CLASS_NAME}__image-modal`}
                closeDialog={closeImageModal}
                isVisible={isImageModalOpen}
                onConfirm={handleImageConfirmed}
                uploadPathPrefix={userUploadId}
            />
        </div>
    );
};

export default ChecklistItemNoteRichTextEditor;
